/* Narrowing the tenant list.
 *
 * The main screen is one row per salon and the only way to find anything on it,
 * so what it filters on is a product decision rather than a convenience. Three
 * questions get their own control because they are the ones actually asked:
 *
 *   is this one live?         suspended salons stay in the list rather than
 *                             disappearing, because "why can't they sign in" is
 *                             the question that brings you here.
 *   is it connected?          a salon with no booking system, or with a mailbox
 *                             nobody is polling, is a salon that looks broken to
 *                             the person using it and fine from the outside.
 *   has anything happened?    a tenant with no turns and no sync is either brand
 *                             new or abandoned, and both want finding.
 *   what have they bought?    a licence about to run out is the only thing on
 *                             this screen with a deadline on it, and "who is up
 *                             for renewal" cannot be answered any other way.
 *
 * Pure functions over the rows `admin_tenant_overview()` returns — see
 * admin/server/schema/12-admin-functions.sql for what is in one. No Vue, no
 * Supabase, so the matching is testable on its own.
 */

// Matched against the name, the note, the mailbox host, the plan and the id.
// The id is there because an id is what an error message or a log line hands
// you, and pasting one in should find its salon rather than nothing.
function matchesQuery(t, query) {
  var q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  var hay = [t.name, t.note, t.scheduling_plugin,
             t.license_plan_name, t.license_plan_id, t.id]
    .filter(Boolean).join(' ').toLowerCase();
  return hay.indexOf(q) !== -1;
}

function matchesStatus(t, status) {
  if (status === 'live') return !t.suspended_at;
  if (status === 'suspended') return !!t.suspended_at;
  return true;
}

/* `connection` is deliberately one control with several answers rather than a
 * row of switches: the useful queries are single ones ("who has no booking
 * system"), and a screen of toggles that can be combined into contradictions is
 * a screen that mostly shows an empty list.
 */
function matchesConnection(t, connection) {
  switch (connection) {
    case 'scheduling':    return !!t.scheduling_on;
    case 'no-scheduling': return !t.scheduling_on;
    case 'ingest':        return (t.ingest_key_count || 0) > 0;
    case 'no-ingest':     return (t.ingest_key_count || 0) === 0;
    default:              return true;
  }
}

// "Quiet" is a salon nothing has been recorded for in a fortnight — no turn
// started and no appointment synced. Both, not either: a salon that is closed
// for a week still has its book syncing, and a salon whose connector broke is
// still starting turns by hand. Only when both have stopped is it worth asking
// about.
var QUIET_DAYS = 14;

function isQuiet(t, now) {
  var cutoff = (now || Date.now()) - QUIET_DAYS * 86400000;
  var turn = t.last_turn_at ? new Date(t.last_turn_at).getTime() : 0;
  var sync = t.last_synced_at ? new Date(t.last_synced_at).getTime() : 0;
  return Math.max(turn, sync) < cutoff;
}

function matchesActivity(t, activity, now) {
  if (activity === 'quiet') return isQuiet(t, now);
  if (activity === 'active') return !isQuiet(t, now);
  return true;
}

/* The licence reading, which arrives with the row from `admin_license_overview`
 * — see loadTenants in js/store.js.
 *
 * `license_state` is NULL when this app could not read licences at all, which
 * is what a project without part 14 of the schema answers. That is not the same
 * as 'none' and must not be filtered as it: a build talking to a database that
 * has never heard of licensing would otherwise report every salon as unlicensed
 * and, worse, hide them all behind "Licensed". So a null state matches only the
 * "any" arm, and every other arm skips it.
 *
 * `expiring` is not a state the database returns — it is `active` with a
 * deadline near enough to be worth a phone call, and the window is the same
 * fortnight js/rules/licensing.js uses so the list and the salon's own page
 * cannot disagree about whether something is nearly up.
 */
var EXPIRING_DAYS = 14;

function licenseExpiring(t, now) {
  if (t.license_state !== 'active' || !t.license_expires_at) return false;
  var left = new Date(t.license_expires_at).getTime() - (now || Date.now());
  return left > 0 && left <= EXPIRING_DAYS * 86400000;
}

function matchesLicense(t, license, now) {
  if (!license || license === 'any') return true;
  if (t.license_state == null) return false;
  switch (license) {
    case 'licensed': return t.license_state === 'active';
    case 'expiring': return licenseExpiring(t, now);
    case 'lapsed':   return t.license_state === 'lapsed';
    case 'none':     return t.license_state === 'none';
    default:         return true;
  }
}

function filterTenants(list, f, now) {
  var opts = f || {};
  return (list || []).filter(function (t) {
    return matchesQuery(t, opts.query) &&
      matchesStatus(t, opts.status) &&
      matchesConnection(t, opts.connection) &&
      matchesActivity(t, opts.activity, now) &&
      matchesLicense(t, opts.license, now);
  });
}

function time(v) { return v ? new Date(v).getTime() : 0; }

/* Sorted with a stable tiebreak on name, so two salons with no turns do not
 * swap places between renders — a list that reorders under the cursor is a list
 * that gets misclicked.
 */
var SORTS = {
  name:     function (a, b) { return a.name.localeCompare(b.name); },
  created:  function (a, b) { return time(b.created_at) - time(a.created_at); },
  activity: function (a, b) { return time(b.last_turn_at) - time(a.last_turn_at); },
  turns:    function (a, b) { return (b.turn_count || 0) - (a.turn_count || 0); }
};

function sortTenants(list, by) {
  var cmp = SORTS[by] || SORTS.name;
  return (list || []).slice().sort(function (a, b) {
    return cmp(a, b) || a.name.localeCompare(b.name);
  });
}

// What the count line under the filters says. Written here rather than in the
// template because "3 of 12" and "12 salons" are the same sentence with a
// condition on it, and a template with a condition in it is where the plural
// bugs live.
function summarise(shown, total) {
  if (shown === total) return total + (total === 1 ? ' tenant' : ' tenants');
  return shown + ' of ' + total + ' tenants';
}

export const tenantFilter = {
  QUIET_DAYS: QUIET_DAYS,
  EXPIRING_DAYS: EXPIRING_DAYS,
  isQuiet: isQuiet,
  licenseExpiring: licenseExpiring,
  filterTenants: filterTenants,
  sortTenants: sortTenants,
  summarise: summarise
};
